import React, { useRef, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./QuillEditor.css"; // Custom CSS for image resizing

const QuillEditor = ({ value, onChange }) => {
  const quillRef = useRef(null);
  const [editorHtml, setEditorHtml] = useState(value || "");

  useEffect(() => {
    setEditorHtml(value); // Update the editor content when the value prop changes
  }, [value]);

  useEffect(() => {
    if (quillRef.current) {
      setupImageHandlers();
    }
    return () => removeResizeHandles(); // Cleanup on component unmount
  }, []);

  const setupImageHandlers = () => {
    const quill = quillRef.current.getEditor();
    quill.getModule("toolbar").addHandler("image", handleInsertImage);

    quill.on("selection-change", (range) => {
      if (range) {
        const [leaf] = quill.getLeaf(range.index);
        if (leaf && leaf.domNode && leaf.domNode.tagName === "IMG") {
          addResizeHandles(leaf.domNode);
        } else {
          removeResizeHandles();
        }
      } else {
        removeResizeHandles();
      }
    });
  };

  const addResizeHandles = (imageNode) => {
    removeResizeHandles(); // Ensure any existing overlays are removed
    const overlay = document.createElement("div");
    overlay.className = "image-resize-overlay";
    document.body.appendChild(overlay);
    positionOverlay(overlay, imageNode);

    const resizeHandles = document.createElement("div");
    resizeHandles.className = "image-resize-handles";
    overlay.appendChild(resizeHandles);

    const onMouseMove = (e) => {
      const rect = imageNode.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      const newHeight = e.clientY - rect.top;
      imageNode.style.width = `${newWidth}px`;
      imageNode.style.height = `${newHeight}px`;
      positionOverlay(overlay, imageNode); // Update overlay position and size
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    resizeHandles.addEventListener("mousedown", () => {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  };

  const positionOverlay = (overlay, imageNode) => {
    const rect = imageNode.getBoundingClientRect();
    overlay.style.top = `${rect.top + window.scrollY}px`; // Adjust for scroll position
    overlay.style.left = `${rect.left + window.scrollX}px`; // Adjust for scroll position
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
  };

  const removeResizeHandles = () => {
    document.querySelectorAll(".image-resize-overlay").forEach((overlay) => overlay.remove());
  };

  const handleInsertImage = () => {
    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
        uploadPreset: "Article_images",
        sources: ["local", "url"],
        multiple: false,
      },
      (error, result) => {
        if (!error && result && result.event === "success") {
          const imageUrl = result.info.secure_url;
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();
          quill.insertEmbed(range.index, "image", imageUrl);
          // Ensure the image is inline by adjusting its style
          const editor = document.querySelector(".ql-editor");
          const insertedImage = editor.querySelector(`img[src="${imageUrl}"]`);
          if (insertedImage) {
            insertedImage.style.display = "inline"; // Ensure inline behavior
            insertedImage.style.verticalAlign = "middle"; // Align with text
            insertedImage.style.margin = "0 5px"; // Add spacing for better positioning
          }
        }
      }
    );
  };

  return (
    <div>
      <ReactQuill
        ref={quillRef}
        value={editorHtml}
        onChange={(content) => {
          setEditorHtml(content);
          onChange(content);
        }}
        modules={{
          toolbar: [
            [{ font: [] }], // Font selector
            [{ size: ["small", false, "large", "huge"] }], // Text size selector
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            ["image", "code-block"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            [{ color: [] }, { background: [] }],
            ["link"],
            ["clean"],
          ],
        }}
        placeholder="Write your article content here..."
      />
    </div>
  );
};

export default QuillEditor;
